import { Show, createMemo } from 'solid-js'

import { useI18n } from '../../i18n'
import { dispatchActiveTabChange } from '../../runtimeBridge'
import {
   type AppTab,
   activeTab,
   isTabMenuCollapsed,
   setActiveTab,
   setTabMenuCollapsed,
} from '../../state/appUiStore'
import { getOpenHabitTaskCount, habits } from '../../state/habitsStore'
import { createLiveClock } from '../../utils/liveClock'

const HABIT_COUNT_REFRESH_MS = 60 * 1000

/**
 * Renders the bottom/side tab navigation.
 *
 * @returns The navigation element.
 */
const TabNavigation = () => {
   const t = useI18n()
   const [currentTime] = createLiveClock({ intervalMs: HABIT_COUNT_REFRESH_MS })
   /**
    * Activates the selected tab.
    *
    * @param tab - The tab to activate.
    */
   const handleSelectTab = (tab: AppTab) => {
      setActiveTab(tab)
      dispatchActiveTabChange(tab)
      if (typeof window !== 'undefined' && window.innerWidth < 640) {
         setTabMenuCollapsed(true)
      }
   }

   /**
    * Returns the CSS classes for a tab button.
    *
    * @param tab - The tab.
    * @returns The class string.
    */
   const tabClass = (tab: AppTab) =>
      `w-full py-2 text-center font-semibold border-b-2 transition-colors duration-300 ${
         activeTab() === tab ? 'tab-active' : 'tab-inactive'
      }`

   const unfulfilledHabitCount = createMemo(() =>
      getOpenHabitTaskCount(habits(), currentTime()),
   )

   return (
      <div
         id="tab-button-group"
         data-collapsed={isTabMenuCollapsed() ? 'true' : 'false'}
         class="mb-2 flex flex-col gap-2 border-b border-edge-soft pb-2 sm:pb-0 lg:col-span-2"
      >
         <button
            id="current-tab-btn"
            class={tabClass('current')}
            onClick={() => handleSelectTab('current')}
         >
            {t('Current Fast')}
         </button>
         <button
            id="history-tab-btn"
            class={tabClass('history')}
            onClick={() => handleSelectTab('history')}
         >
            {t('History')}
         </button>
         <button
            id="habits-tab-btn"
            class={tabClass('habits')}
            onClick={() => handleSelectTab('habits')}
         >
            <span class="inline-flex items-center justify-center gap-2">
               <span>{t('Habits')}</span>
               <Show when={unfulfilledHabitCount() > 0}>
                  <span
                     class="inline-grid min-w-6 place-items-center rounded-md bg-emerald-500 px-1.5 py-0.5 text-xs font-bold leading-none text-ink-inverse shadow-sm shadow-emerald-950/30"
                     aria-label={t('{count} habits due', {
                        count: unfulfilledHabitCount(),
                     })}
                  >
                     {unfulfilledHabitCount()}
                  </span>
               </Show>
            </span>
         </button>
         <button
            id="measurements-tab-btn"
            class={tabClass('measurements')}
            onClick={() => handleSelectTab('measurements')}
         >
            {t('Measurements')}
         </button>
         <button
            id="account-tab-btn"
            class={tabClass('account')}
            onClick={() => handleSelectTab('account')}
         >
            {t('Account')}
         </button>
      </div>
   )
}

export default TabNavigation
